import type { ReactNode, FC } from 'react';
import React, { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { layers } from '@atlaskit/theme/constants';
import { css } from '@atlaskit/css';
import { Text, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import { OnboardingNudge } from '@confluence/onboarding-nudge-tooltip/entry-points/OnboardingNudgeTooltip';

export const i18n = defineMessages({
	title: {
		id: 'share-and-restrict-dialog.share-nudge-publish.title',
		defaultMessage: 'Your work deserves an audience',
		description: 'Bolded text shown at the top of the spotlight, similar to a header.',
	},
	body: {
		id: 'share-and-restrict-dialog.share-nudge-publish.body',
		defaultMessage: 'Share this page with someone so they can contribute or simply view it.',
		description: 'Text shown in a spotlight message body',
	},
	cta: {
		id: 'share-and-restrict-dialog.share-nudge-publish.cta',
		defaultMessage: 'OK',
		description: 'This message is shown as text on a button in a spotlight',
	},
});

// allows the nudge to properly wrap the whole modal
const NudgeAnchorWrapper = css({
	borderColor: 'transparent',
	borderStyle: 'solid',
	borderWidth: token('border.width'),
});

type ShareAndRestrictDialogNudgeProps = {
	children: ReactNode;
};

const ContextualNudgeContent = () => {
	return (
		<Stack space="space.100">
			<Heading size="medium" color="color.text.inverse">
				<FormattedMessage {...i18n.title} />
			</Heading>
			<Text color="color.text.inverse">
				<FormattedMessage {...i18n.body} />
			</Text>
		</Stack>
	);
};

const ShareAndRestrictDialogNudge: FC<ShareAndRestrictDialogNudgeProps> = ({ children }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isHidden, setIsHidden] = useState(false);

	return (
		<OnboardingNudge
			hidden={isHidden}
			setHidden={() => {
				setIsHidden(true);
			}}
			content={<ContextualNudgeContent />}
			actionSubjectId="contextualInviteNudge"
			source="share-and-restrict-dialog-nudge"
			width={275}
			actions={[
				{
					onClick: () => {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'shareAndRestrictDialogNudgeOk',
								source: 'shareAndRestrictDialog',
							},
						}).fire();
						setIsHidden(true);
					},
					text: formatMessage(i18n.cta),
				},
			]}
			position="right-start"
			onShow={() => {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'shareAndRestrictDialogNudge',
					},
				}).fire();
			}}
			zIndex={layers.modal()}
		>
			<div css={NudgeAnchorWrapper}>{children}</div>
		</OnboardingNudge>
	);
};

export const ShareAndRestrictDialogNudgeWrapper = ({
	children,
	showDialogNudgeSpotlight,
}: {
	children: ReactNode;
	showDialogNudgeSpotlight?: boolean;
}) => {
	return showDialogNudgeSpotlight ? (
		<ShareAndRestrictDialogNudge>{children}</ShareAndRestrictDialogNudge>
	) : (
		children
	);
};
